import { Button, Card, Checkbox, FileDrop, Icon, Input, Modal, Select, Table } from '@scuf/common';
import { LabelDescription } from 'components';
import Alert from 'components/alert';
import { RmaModelLiteMf, shippingTypeMf, typeSituationMf, statusOptionsMf, approvalStatusOptions } from 'models';
import { rmaItemsMf } from 'models/rmamanufatura/detailsRma';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { notificationAtom } from 'resources';
import { RMAMANUFATURA, COMPANY_MANAGER } from 'resources';
import useFetch from 'helpers/hooks/useFetch';
import useUser from 'helpers/hooks/useUser';
import 'styles/rmaManufatura.scss';
import { CompanyDTO } from 'models/userManager/addCustomer';

interface ChangeStatusProps {
    rmaState: RmaModelLiteMf;
    setRmaState: React.Dispatch<React.SetStateAction<RmaModelLiteMf>>;
}

function Processing({ rmaState, setRmaState }: ChangeStatusProps): JSX.Element {
    const [modalState, setModalState] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const setNotification = useSetRecoilState(notificationAtom);
    const { t } = useTranslation(['rmamanufatura', 'common']);
    const [file, setFile] = useState<File | null>(null);
    const { user } = useUser();
    const [approvalModalOpen, setApprovalModalOpen] = useState<boolean>(false);
    const [nextApprovalStatus, setNextApprovalStatus] = useState<number>(0);
    const [observation, setObservation] = useState<string>('');
    const [observationError, setObservationError] = useState<string>('');
    const [frete, setFrete] = useState<string>('');
    const [freteError, setFreteError] = useState<string>('');

    const [, getRmaManufacturingDetails] = useFetch<{ rmaId: number }, { data: RmaModelLiteMf }>({
        url: RMAMANUFATURA.getRmaManufacturingDetails,
        redirectOnError: true
    });

    const [responseCompany, getCompanyById, loadingCompany] = useFetch<{ id: number | undefined }, CompanyDTO>({
        url: COMPANY_MANAGER.getCompanyById,
        method: 'GET',
        onError: 'EmailError'
    });

    const [, ProcessingRmaManufacturing] = useFetch<{
        id: number;
        email: any;
        situation: string;
        shipping: string;
        nextStatus: number;
        statusId: number;
        observation: string;
        rmaItemsManufacturing: rmaItemsMf[];
        ssoId: string;
        companyName: string;
        cnpjCompany: string;
        frete: number | null;
    }>({
        url: RMAMANUFATURA.processingRmaManufacturing,
        method: 'POST',
        onError: 'Erro ao alterar situação'
    });

    async function sendRmaByAdmin() {
        if (nextApprovalStatus === 10 && !observation.trim()) {
            setObservationError('Campo obrigatório para status Finalizado');
            return;
        }

        setLoading(true);

        try {
            const statusIdToSend = Number(nextApprovalStatus);

            await ProcessingRmaManufacturing({
                id: rmaState.id,
                email: responseCompany?.data?.users?.find(x => x.ssoId === rmaState.ssoId)?.email,
                situation: rmaState.situation,
                shipping: rmaState.shipping,
                nextStatus: statusIdToSend,
                statusId: statusIdToSend,
                observation: observation,
                rmaItemsManufacturing: rmaState.rmaItemsManufacturing,
                ssoId: user?.ssoid || '-',
                companyName: responseCompany?.data?.name!,
                cnpjCompany: responseCompany?.data?.cnpj!,
                frete: frete === '' ? null : Number(frete)
            });

            setNotification(old => ({ ...old, message: 'Status alterado com sucesso', type: 'success' }));
            window.location.reload();
        } catch (error) {
            setNotification(old => ({ ...old, message: 'Erro ao alterar status' }));
        }
        setLoading(false);
        setApprovalModalOpen(false);
    }

    function renderModal(): JSX.Element {
        return (
            <Modal
                className="modal"
                onClose={() => setModalState(false)}
                size="small"
                open={modalState}
                closeOnDimmerClick={true}
                closeOnDocumentClick={true}
            >
                <Card.Footer>
                    <FileDrop
                        placeholder={t('common:actions.Draganddropfileshere')}
                        resetButtonText={t('common:actions.Delete')}
                        onReset={() => setFile(null)}
                        onAccepted={file => {
                            setFile(file[0]);
                        }}
                    />
                    <br />
                    <div className="buttonModal">
                        <Button
                            content={file ? t('common:actions.Finish') : t('common:actions.Close')}
                            onClick={() => setModalState(false)}
                            size="medium"
                            textTransform={true}
                        />
                    </div>
                </Card.Footer>
            </Modal>
        );
    }

    function renderApprovalModal(): JSX.Element {
        return (
            <Modal
                onClose={() => setApprovalModalOpen(false)}
                size="small"
                open={approvalModalOpen}
                closeOnDimmerClick={false}
                closeOnDocumentClick={false}
            >
                <Modal.Header>{t('rmamanufatura:translation.processOrder')}</Modal.Header>
                <Modal.Content>
                    <div className="approval-modal-content">
                        <Select
                            fluid
                            indicator="required"
                            label="Próximo Status de Aprovação"
                            placeholder="Selecione o próximo status"
                            options={approvalStatusOptions}
                            value={nextApprovalStatus}
                            onChange={(value: any) => {
                                const statusId =
                                    typeof value === 'object' && value !== null ? Number(value.value) : Number(value);
                                setNextApprovalStatus(statusId);
                            }}
                        />
                        <Input
                            fluid
                            placeholder="Observação"
                            label="Observação"
                            value={observation}
                            error={observationError || undefined}
                            helperText={observationError || undefined}
                            onChange={(value: string) => {
                                setObservation(value);
                                if (value.trim()) {
                                    setObservationError('');
                                }
                            }}
                            maxLength={500}
                        />
                    </div>
                </Modal.Content>
                <Modal.Footer>
                    <Button content="Cancelar" type="secondary" onClick={() => setApprovalModalOpen(false)} size="medium" />
                    <Button
                        content="Confirmar"
                        loading={loading}
                        disabled={!nextApprovalStatus}
                        onClick={() => sendRmaByAdmin()}
                        size="medium"
                    />
                </Modal.Footer>
            </Modal>
        );
    }

    function handleProcessClick() {
        getCompanyById({ id: rmaState.companyId });
        if (rmaState?.situation === 'APROVADO') {
            setApprovalModalOpen(true);
        } else {
            sendRmaByAdmin();
        }
    }

    const isFormValid = () => {
        return !!rmaState.situation && !!rmaState.shipping;
    };

    function validateFrete(value: string): string {
        if (value.includes(',')) {
            return 'Utilize ponto ao invés de vírgula para casas decimais!';
        }
        if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
            return '';
        }
        return 'Valor inválido';
    }

    return (
        <div className="container-changes">
            {renderModal()}
            {renderApprovalModal()}
            <div className="container-selects">
                {rmaState?.statusId === 1 || (rmaState?.statusId > 50 && rmaState?.statusId < 54) ? (
                    [
                        <div key="processing-container">
                            <div className="container-selects">
                                <div className="div-datatable">
                                    <h2>{t('common:translation.Items')}</h2>
                                    <Table>
                                        <Table.Header>
                                            <Table.HeaderCell>{t('common:translation.Model')}</Table.HeaderCell>
                                            <Table.HeaderCell>{t('common:translation.SerialNumber')}</Table.HeaderCell>
                                            <Table.HeaderCell>
                                                {t('rmamanufatura:manage_eos.translation.ProblemLocation')}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell>{t('common:translation.Description')}</Table.HeaderCell>
                                            <Table.HeaderCell>{t('common:translation.Quantity')}</Table.HeaderCell>
                                            <Table.HeaderCell>{t('common:status.Approved')}</Table.HeaderCell>
                                        </Table.Header>
                                        <Table.Body>
                                            {rmaState?.rmaItemsManufacturing?.map((item, index) => {
                                                const equipment = rmaState?.rmaItemsEquipmentManufacturing?.find(
                                                    eq => eq.rmaItemsManufacturingId === item.id
                                                );
                                                return (
                                                    <Table.Row key={item.id || index}>
                                                        <Table.Cell>{item?.model || '--'}</Table.Cell>
                                                        <Table.Cell>{equipment?.serialNumber || '--'}</Table.Cell>
                                                        <Table.Cell>
                                                            {equipment?.problemLocation?.name ||
                                                                equipment?.problemLocationId ||
                                                                '--'}
                                                        </Table.Cell>
                                                        <Table.Cell>{equipment?.problemTypeDescription || '--'}</Table.Cell>
                                                        <Table.Cell>{equipment?.quantity || '0'}</Table.Cell>
                                                        <Checkbox
                                                            checked={item?.approved}
                                                            onChange={(value: boolean) => {
                                                                setRmaState(old => ({
                                                                    ...old,
                                                                    rmaItemsManufacturing: old.rmaItemsManufacturing.map(e =>
                                                                        e.id === item.id ? { ...e, approved: value } : e
                                                                    )
                                                                }));
                                                            }}
                                                        ></Checkbox>
                                                    </Table.Row>
                                                );
                                            })}
                                        </Table.Body>
                                    </Table>
                                </div>
                                <br></br>
                                <div>
                                    <h2>{t('rma.budget.ShippingTransportation')}</h2>
                                </div>
                                <br></br>
                                <div className="form3Columns">
                                    <Select
                                        className="select"
                                        options={shippingTypeMf}
                                        label={t('rma.shipping.Shipping')}
                                        placeholder={t('rma.shipping.Shipping')}
                                        value={rmaState?.shipping}
                                        onChange={(value: number) => {
                                            setRmaState(old => ({
                                                ...old,
                                                shipping: value.toString()
                                            }));
                                        }}
                                    />
                                    <Select
                                        className="select"
                                        indicator="required"
                                        placeholder={t('common:status.Situation')}
                                        options={typeSituationMf}
                                        label={t('common:status.Situation')}
                                        value={rmaState.situation}
                                        onChange={(value: any) => {
                                            setRmaState(old => ({
                                                ...old,
                                                situation: value.toString()
                                            }));
                                        }}
                                    />

                                    <Input
                                        placeholder={t('rma.order.PostCode')}
                                        label={t('rma.order.PostCode')}
                                        maxLength={30}
                                        max={30}
                                    ></Input>
                                </div>
                                <div className="form3Columns">
                                    <Input
                                        label={t('translation.Freight')}
                                        placeholder={t('translation.Freight')}
                                        value={frete}
                                        error={freteError || undefined}
                                        min={0}
                                        onChange={(value: string) => {
                                            setFrete(value);
                                            setFreteError(validateFrete(value));
                                        }}
                                    />
                                    <Input
                                        placeholder={t('rma.order.Observations')}
                                        label={t('rma.order.Observations')}
                                        helperText={t('rmamanufatura:translation.ObservationsHelp')}
                                        maxLength={300}
                                        max={300}
                                        value={observation}
                                        error={observationError || undefined}
                                        onChange={(value: string) => {
                                            setObservation(value);
                                            if (value.trim()) {
                                                setObservationError('');
                                            }
                                        }}
                                    />
                                </div>
                                {file && (
                                    <LabelDescription title={t('common:translation.File')}>
                                        <>
                                            {file?.name}{' '}
                                            <Icon
                                                name="delete"
                                                onClick={() => {
                                                    setFile(null);
                                                }}
                                            ></Icon>
                                        </>
                                    </LabelDescription>
                                )}
                            </div>
                            <div className="container-button">
                                {/* <Button
                                    type="inline-secondary"
                                    icon="attachment"
                                    onClick={() => setModalState(true)}
                                    content={t('common:actions.Add')}
                                    iconPosition="right"
                                /> */}
                                <Button
                                    content={t('common:actions.SetProcees')}
                                    loading={loading}
                                    onClick={handleProcessClick}
                                    disabled={!isFormValid()}
                                />
                            </div>
                        </div>
                    ]
                ) : (
                    <Alert text={t('common:translation.RmaNotProcess')} />
                )}
            </div>
        </div>
    );
}
export default Processing;
